import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth";
import "./DeactivateAccount.css";
import axios from "axios";

const DEACTIVATE_REASONS = [
  { value: "no_longer_needed", label: "I no longer need this account" },
  { value: "another_account", label: "I have another account" },
  { value: "privacy_concern", label: "Privacy concern" },
  { value: "other", label: "Other" },
];

const DeactivateAccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [successfulDeactivate, setSuccessfulDeactivate] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (showWarningModal || successfulDeactivate || showFailureModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [showWarningModal, successfulDeactivate, showFailureModal]);

  const finalReason = useMemo(() => {
    if (!reason) return "";
    if (reason === "other") return otherReason.trim();
    return reason;
  }, [reason, otherReason]);

  const isDisabled =
    !reason || loading || (reason === "other" && !otherReason.trim());

  const handleBack = () => navigate("/account");

  const handleDeactivateClick = () => {
    if (isDisabled) return;
    setShowWarningModal(true);
  };

  const handleConfirmDeactivation = async () => {
    if (!finalReason) return;

    setLoading(true);

    try {
      await axios.post("/user/request-deactivate-account", {
        userId: user._id,
        reason: finalReason,
      });

      setSuccessfulDeactivate(true);
      setShowWarningModal(false);
    } catch (error) {
      console.error("Account deactivation failed:", error);
      setShowFailureModal(true);
      setShowWarningModal(false);
    } finally {
      setLoading(false);
    }
  };

  const finalizeDeactivate = async () => {
    try {
      await logout();
      navigate("/", { replace: true });
    } catch (error) {
      // console.error("Error finalizing deactivation:", error);
    }
  };

  const handleTryAgain = () => {
    setShowFailureModal(false);
    handleConfirmDeactivation();
  };

  return (
    <div className="app-layout">
      <div className="main-content">
        <div className="deactivate-page">
          <div className="deactivate-header">
            <button className="deactivate-back-button" onClick={handleBack}>
              ← Back
            </button>
            <h1>Account Deactivation</h1>
          </div>

          <div className="deactivate-content">
            <h2>Deactivate Account?</h2>
            <p className="deactivate-description">
              Once this process is activated, you will lose access to your
              account along with all associated events and information. If you
              wish to regain access, please contact our support team at{" "}
              <a
                href="mailto:nimblebashsupport@nimblebash.com"
                className="deactivate-security-support"
              >
                nimblebashsupport@nimblebash.com
              </a>
            </p>

            <div className="deactivate-reason-section">
              <h3>Select a reason</h3>

              <div className="deactivate-reason-options">
                {DEACTIVATE_REASONS.map((r) => (
                  <label key={r.value} className="deactivate-reason-option">
                    <input
                      type="radio"
                      name="reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                    />
                    {r.label}
                  </label>
                ))}
              </div>

              {reason === "other" && (
                <div className="deactivate-other-reason">
                  <label>Other Information (Optional)</label>
                  <textarea
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    placeholder="Anything else you'd like to tell us..."
                    rows={4}
                  />
                </div>
              )}
            </div>

            <div className="deactivate-actions">
              <button
                className="deactivate-back-button-secondary"
                onClick={handleBack}
              >
                Back
              </button>

              <button
                className="deactivate-button"
                onClick={handleDeactivateClick}
                disabled={isDisabled}
              >
                {loading ? "Processing..." : "Deactivate Account"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showWarningModal && (
        <div className="deactivate-modal-overlay">
          <div className="deactivate-modal-content warning-modal">
            {!loading && (
              <button
                className="deactivate-modal-close"
                onClick={() => setShowWarningModal(false)}
              >
                ×
              </button>
            )}

            <h2 className="deactivate-modal-title">
              Warning: Account Deactivation
            </h2>

            <p className="deactivate-modal-message">
              Deactivating your account will result in loss of access and
              associated data. Are you sure you want to proceed?
            </p>
            {loading && (
              <p className="deactivate-processing-text">
                Processing your deactivation request...
              </p>
            )}

            <div className="deactivate-modal-actions">
              <button
                className="deactivate-modal-button deactivate-back-btn"
                onClick={() => setShowWarningModal(false)}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                className="deactivate-modal-button deactivate-btn"
                onClick={handleConfirmDeactivation}
                disabled={loading}
              >
                {loading ? "Processing..." : "Deactivate Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFailureModal && (
        <div className="deactivate-modal-overlay">
          <div className="deactivate-modal-content deactivate-failure-modal">
            <button
              className="deactivate-modal-close"
              onClick={() => setShowFailureModal(false)}
            >
              ×
            </button>

            <h2 className="deactivate-modal-title">Failed</h2>
            <p className="deactivate-modal-message">
              Your account deactivation request failed. Please try again.
            </p>

            <div className="deactivate-modal-actions">
              <button
                className="deactivate-modal-button deactivate-try-again-btn"
                onClick={handleTryAgain}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {successfulDeactivate && (
        <div className="deactivate-modal-overlay">
          <div className="deactivate-modal-content deactivate-warning-modal">
            <div className="deactivate-modal-successful-wrapper">
              <h2 className="deactivate-modal-successful">
                Submitted Successfully
              </h2>
              <p className="deactivate-modal-kindly">
                Kindly check your email for the concluding part of this
                suspension process.
              </p>

              <button
                className="deactivate-modal-button finalize-deactivate"
                onClick={finalizeDeactivate}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeactivateAccountPage;
